import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth, AREAS } from '../contexts/AuthContext';
import { hasAllowedRole } from '../routes/roleAccess';
import { 
  LogOut, 
  Settings,
  Menu, 
  X, 
  User,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import UserStatusDropdown from '../components/shared/UserStatusDropdown';
import { fetchAvailabilityTypes } from '../api';
import AlertNotifications from '../components/AlertNotifications';

// Íconos KIO para submenús (rutas de imagen)
const KIO = {
  dashboard:    '/assets/icons/KIO_Business Chart.png',
  inventario:   '/assets/icons/KIO_Database.png',
  calendario:   '/assets/icons/KIO_Clock .png',
  monitoreo:    '/assets/icons/KIO_Synchronize.png',
  ticketsControl: '/assets/icons/KIO_IA.png',
  objetivos:    '/assets/icons/KIO_Target.png',
  renovaciones: '/assets/icons/KIO_SettingTime.png',
  timesheets:   '/assets/icons/KIO_Time.png',
  calidad:      '/assets/icons/KIO_Check Shield.png',
  agente:       '/assets/icons/KIO_IA.png',
  alertas:      '/assets/icons/KIO_Synchronize.png',
  alianzas:     '/assets/icons/KIO_Users People1.png',
  automatizaciones: '/assets/icons/KIO_Rute.png',
  proyectos: '/assets/icons/KIO_Idea.png',
};

// Filtros para íconos de submenú: gris inactivo, blanco activo
const SUB_FILTER        = 'brightness(0) invert(1) opacity(0.45)';
const SUB_FILTER_ACTIVE = 'brightness(0) invert(1) opacity(1)';

// ─── Colores por área ─────────────────────────────────────────────────────────
// ── Filtros CSS para colorear PNGs monocromáticos KIO ────────────────────────
// Cada área recibe su propio filtro de color que coincide con su acento.
// Herramienta usada para calcular: https://codepen.io/sosuke/pen/Pjoqqp
const COLOR_FILTERS = {
  purple:  'brightness(0) saturate(100%) invert(44%) sepia(80%) saturate(600%) hue-rotate(230deg) brightness(110%)',   // #8644ff
  lavender:'brightness(0) saturate(100%) invert(70%) sepia(30%) saturate(400%) hue-rotate(230deg) brightness(120%)',   // #c88dff
  pink:    'brightness(0) saturate(100%) invert(20%) sepia(90%) saturate(700%) hue-rotate(300deg) brightness(120%)',   // #ff0080
  cyan:    'brightness(0) saturate(100%) invert(80%) sepia(60%) saturate(400%) hue-rotate(150deg) brightness(110%)',   // #04d9ff
  green:   'brightness(0) saturate(100%) invert(55%) sepia(90%) saturate(500%) hue-rotate(90deg)  brightness(115%)',   // #00d60a
  orange:  'brightness(0) saturate(100%) invert(65%) sepia(80%) saturate(600%) hue-rotate(10deg)  brightness(110%)',   // #ff9b00
};

const AREA_CONFIG = {
  [AREAS.ADMIN]: {
    name: 'Administración',
    // KIO_Users People1.png — ícono de personas/equipo
    icon: '/assets/icons/KIO_Users People1.png',
    iconBg: 'rgba(134,68,255,0.18)',
    iconFilter: COLOR_FILTERS.purple,
    activeAccent: '#8644ff',
    activeLabelColor: '#c9a8ff',
    path: '/admin',
    isImage: true
  },
  [AREAS.ALIANZAS]: {
    name: 'Alianzas',
    icon: '/assets/icons/KIO_Users People1.png',
    iconBg: 'rgba(0,184,255,0.15)',
    iconFilter: 'brightness(0) saturate(100%) invert(60%) sepia(90%) saturate(2000%) hue-rotate(175deg) brightness(105%)',
    activeAccent: '#00B8FF',
    activeLabelColor: '#8fdcff',
    path: '/alianzas',
    isImage: true,
    hasSubmenu: true,
    submenu: [
      {
        name: 'Public Cloud & Alliances QBR',
        kioIcon: 'dashboard',
        path: '/alianzas/qbr',
        permission: 'alianzas.qbr.view'
      },
      {
        name: 'Gestión QBR',
        kioIcon: 'calidad',
        path: '/alianzas/qbr-gestion',
        permission: 'alianzas.qbr.gestion.view'
      },
      {
        name: 'Seguimiento Alianzas',
        kioIcon: 'alianzas',
        path: '/alianzas/seguimiento',
        permission: 'alianzas.seguimiento.view'
      },
      {
        name: 'Certificaciones',
        kioIcon: 'calidad',
        path: '/alianzas/certificaciones',
        permission: 'alianzas.certificaciones.view'
      }
    ]
  },
  [AREAS.KLOUD_OPS]: {
    name: 'Kloud Público OPS',
    // KIO_cloud.png — nube KIO outline
    icon: '/assets/icons/KIO_cloud.png',
    iconBg: 'rgba(200,141,255,0.15)',
    iconFilter: COLOR_FILTERS.lavender,
    activeAccent: '#c88dff',
    activeLabelColor: '#e8d8ff',
    path: '/kloud-ops',
    isImage: true,
    hasSubmenu: true,
    submenu: [
      { name: 'Dashboard',            kioIcon: 'dashboard',     path: '/kloud-ops/dashboard',       permission: 'ops.dashboard.basic' },
      { name: 'Clientes OPS',         kioIcon: 'dashboard',     path: '/kloud-ops/clientes',        permission: 'ops.dashboard.basic' },
      { name: 'Dashboard Fusión',     kioIcon: 'dashboard',     path: '/kloud-ops/dashboard-fusion', permission: 'ops.dashboard.fusion.view', roles: ['admin', 'kloud_ops_coordinador', 'kloud_ops_gerente'] },
      { name: 'Rentabilidad',         kioIcon: 'dashboard',     path: '/kloud-ops/rentabilidad', permission: 'ops.dashboard.fusion.view', roles: ['admin', 'kloud_ops_coordinador', 'kloud_ops_gerente'] },
      { name: 'Cobertura',            kioIcon: 'monitoreo',     path: '/kloud-ops/cobertura',       permission: 'ops.dashboard.basic' },
      { name: 'Centro de Operaciones', kioIcon: 'alertas',      path: '/kloud-ops/centro-operaciones' },
      { name: 'Calendario de turnos', kioIcon: 'calendario',    path: '/kloud-ops/horas',           permission: 'ops.horas.view' },
      { name: 'Centro de Reportes',   kioIcon: 'monitoreo',     path: '/kloud-ops/centro-reportes', permission: 'ops.monitoreo.view' },
      { name: 'Tickets Control',      kioIcon: 'ticketsControl',path: '/kloud-ops/tickets-control', permission: 'ops.tickets-control.view' },
    ]
  },
  [AREAS.KLOUD_SAAS]: {
    name: 'Kloud Público SaaS',
    // saas-icon.png — ícono original ya existente
    icon: '/assets/icons/saas-icon.png',
    iconBg: 'rgba(255,0,128,0.15)',
    iconFilter: COLOR_FILTERS.pink,
    activeAccent: '#ff0080',
    activeLabelColor: '#ffaacc',
    path: '/kloud-saas',
    isImage: true
  },
  [AREAS.MESA_SPIN]: {
    name: 'Mesa Spin',
    // KIO_Business Chart.png — gráfica de negocio
    icon: '/assets/icons/KIO_Business Chart.png',
    iconBg: 'rgba(4,217,255,0.15)',
    iconFilter: COLOR_FILTERS.cyan,
    activeAccent: '#04d9ff',
    activeLabelColor: '#a0f0ff',
    path: '/mesa-spin',
    isImage: true,
    hasSubmenu: true,
    submenu: [
      { name: 'Dashboard',      kioIcon: 'dashboard', path: '/mesa-spin/dashboard' },
      { name: 'Objetivos 2025', kioIcon: 'objetivos', path: '/mesa-spin/objetivos' }
    ]
  },
  [AREAS.FINOPS]: {
    name: 'FINOPS',
    // KIO_Coin Hand.png — mano con moneda, perfecto para finanzas
    icon: '/assets/icons/KIO_Coin Hand.png',
    iconBg: 'rgba(0,178,17,0.2)',
    iconFilter: COLOR_FILTERS.green,
    activeAccent: '#00d60a',
    activeLabelColor: '#4dff5c',
    path: '/finops',
    isImage: true,
    hasSubmenu: true,
    submenu: [
      { name: 'Dashboard',       kioIcon: 'dashboard',    path: '/finops/dashboard',     permission: 'finops.dashboard' },
      { name: 'Renovaciones',    kioIcon: 'renovaciones', path: '/finops/Renovaciones',  permission: 'finops.renovaciones' },
      { name: 'Timesheets',      kioIcon: 'timesheets',   path: '/finops/timesheets',    permission: 'finops.timesheets' },
      { name: 'Billing',         kioIcon: 'dashboard',    path: '/finops/billing',       permission: 'finops.dashboard' },
      { name: 'Presupuestos',    kioIcon: 'dashboard',    path: '/finops/presupuestos',  permission: 'finops.presupuestos' },
      { name: 'Servicios Cloud', kioIcon: 'inventario',   path: '/finops/servicios-cloud', permission: 'finops.servicios_cloud' },
      { name: 'Calidad de Datos',kioIcon: 'calidad',      path: '/finops/calidad-datos', permission: 'finops.calidad' }
    ]
  },
  [AREAS.QA]: {
    name: 'QA',
    // KIO_Check Shield.png — escudo con check, ideal para QA
    icon: '/assets/icons/KIO_Check Shield.png',
    iconBg: 'rgba(134,68,255,0.15)',
    iconFilter: COLOR_FILTERS.lavender,
    activeAccent: '#c88dff',
    activeLabelColor: '#e8d8ff',
    path: '/qa',
    isImage: true
  },
  [AREAS.SERVICIOS_PROFESIONALES]: {
    name: 'Servicios Profesionales',
    // KIO_Wrench Screwdriver.png — herramientas, servicios técnicos
    icon: '/assets/icons/KIO_Wrench Screwdriver.png',
    iconBg: 'rgba(165,110,255,0.15)',
    iconFilter: 'brightness(0) saturate(100%) invert(58%) sepia(69%) saturate(2500%) hue-rotate(226deg) brightness(103%)',
    activeAccent: '#A56EFF',
    activeLabelColor: '#A56EFF',
    path: '/servicios-profesionales',
    isImage: true,
    hasSubmenu: true,
    submenu: [
      { name: 'IaC Generator', kioIcon: 'automatizaciones', path: '/servicios-profesionales/iac-generator' },
      { name: 'Proyectos Desplegados', kioIcon: 'proyectos', path: '/servicios-profesionales/proyectos-desplegados' },
      { name: 'Repositorios IaC', kioIcon: 'inventario', path: '/servicios-profesionales/repositorios-iac' },
      { name: 'Configuracion', kioIcon: 'renovaciones', path: '/servicios-profesionales/configuracion' },
    ]
  },
  [AREAS.VENTAS]: {
    name: 'Ventas',
    // Usa un ícono relacionado a negocio/ventas. Si tienes uno mejor, cámbialo aquí.
    icon: '/assets/icons/KIO_Business Chart.png',
    iconBg: 'rgba(134,68,255,0.16)',
    iconFilter: COLOR_FILTERS.purple,
    activeAccent: '#8644ff',
    activeLabelColor: '#c9a8ff',
    path: '/ventas',
    isImage: true,
    hasSubmenu: true,
    submenu: [
      {
        name: 'Agente Comercial',
        kioIcon: 'agente',
        path: '/ventas/agente',
        permission: 'ventas.agent.view'
      }
    ]
  },
  [AREAS.GOBIERNO_CLOUD]: {
    name: 'Gobierno Cloud',
    icon: '/assets/icons/KIO_Cloud_Data.png',
    iconBg: 'rgba(14,165,233,0.18)',
    iconFilter: 'brightness(0) saturate(100%) invert(55%) sepia(80%) saturate(500%) hue-rotate(175deg) brightness(110%)',
    activeAccent: '#0ea5e9',
    activeLabelColor: '#7dd3fc',
    path: '/gobierno-cloud',
    isImage: true,
    hasSubmenu: true,
    submenu: [
      {
        name: 'Cloud Accounts',
        kioIcon: 'inventario',
        path: '/gobierno-cloud/cloud-accounts',
        permission: 'gobierno_cloud.cloud_accounts.view'
      }
    ]
  },
  [AREAS.INNOVATION]: {
    name: 'Innovation',
    icon: '/assets/icons/KIO_Idea.png',
    iconBg: 'rgba(255,0,128,0.15)',
    iconFilter: COLOR_FILTERS.pink,
    activeAccent: '#ff0080',
    activeLabelColor: '#ffaacc',
    path: '/innovation',
    isImage: true,
    hasSubmenu: true,
    submenu: [
      {
        name: 'Dashboard Fusión',
        kioIcon: 'proyectos',
        path: '/innovation/dashboard-fusion',
        permission: 'innovation.dashboard.fusion.view'
      },
      {
        name: 'Automatizaciones',
        kioIcon: 'automatizaciones',
        path: '/innovation/automatizaciones',
        permission: 'innovation.automatizaciones.view'
      },
      {
        name: 'Proyectos',
        kioIcon: 'proyectos',
        path: '/innovation/proyectos',
        permission: 'innovation.proyectos.view'
      }
    ]
  },
};

const getInitials = (name = '') =>
  name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();

// ─── Mapea un status string → { label, color } ───────────────────────────────
// Recibe availabilityTypes para resolver estados personalizados como "Capacitación"
function resolveStatusUI(status, availabilityTypes = []) {
  // 1. Buscar en tipos personalizados (GUID o nombre especial)
  const custom = availabilityTypes.find(
    t => t.availability_type_id === status || t.name === status
  );
  if (custom) {
    return {
      label: custom.name,
      color: custom.blocks_assignment ? '#ff5050' : '#ffaa00'
    };
  }

  // 2. Estados base
  switch (status) {
    case 'AVAILABLE': return { label: 'Disponible', color: '#00ff12' };
    case 'BUSY':      return { label: 'Ocupado',    color: '#ff5050' };
    case 'OFF':       return { label: 'Ausente',    color: '#999999' };
    default:          return { label: 'Disponible', color: '#00ff12' };
  }
}

// ─── PortalDropdown ───────────────────────────────────────────────────────────
function PortalDropdown({ anchorRef, open, onClose, user, onStatusChange }) {
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const recalc = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, left: rect.left });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return;
    recalc();
    window.addEventListener('scroll', recalc, true);
    window.addEventListener('resize', recalc);
    return () => {
      window.removeEventListener('scroll', recalc, true);
      window.removeEventListener('resize', recalc);
    };
  }, [open, recalc]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
      <div style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: 9999,
        minWidth: '260px',
        filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))'
      }}>
        <UserStatusDropdown
          user={user}
          onStatusChange={(status) => {
            onStatusChange(status);
            onClose();
          }}
        />
      </div>
    </>,
    document.body
  );
}

// ─── DashboardLayout ──────────────────────────────────────────────────────────
export default function DashboardLayout({ children }) {
  const { user, logout, userAreas, hasPermission } = useAuth();
  const [sidebarOpen, setSidebarOpen]     = useState(() => window.innerWidth > 768);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  // Flyout: área con submenú actualmente en hover (sidebar colapsado)
  const [flyoutArea, setFlyoutArea]       = useState(null);
  const [flyoutCoords, setFlyoutCoords]   = useState({ top: 0 });
  const flyoutTimeout = useRef(null);

  // Estado real del usuario — se carga desde el backend igual que UserStatusDropdown
  const [userStatus, setUserStatus]           = useState('AVAILABLE');
  const [availabilityTypes, setAvailabilityTypes] = useState([]);

  const userTriggerRef = useRef(null);
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Carga availabilityTypes (necesario para resolver "Capacitación", etc.) ──
  useEffect(() => {
    fetchAvailabilityTypes()
      .then(types => setAvailabilityTypes(types))
      .catch(err  => console.error('Error loading availability types:', err));
  }, []);

  // ── Carga el estado REAL desde el backend (igual que UserStatusDropdown) ────
  // Se ejecuta al montar y cada vez que el usuario cambia su estado
  const loadCurrentStatus = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://40.117.79.165:8000'}/workforce/user/${user.id}/current-status`
      );
      if (res.ok) {
        const data = await res.json();
        setUserStatus(data.status || 'AVAILABLE');
      }
    } catch (err) {
      console.error('Error loading current status in layout:', err);
    }
  }, [user?.id]);

  useEffect(() => { loadCurrentStatus(); }, [loadCurrentStatus]);

  // Cierra el dropdown al navegar
  useEffect(() => { setDropdownOpen(false); }, [location.pathname]);

  const toggleSubmenu = (area) =>
    setExpandedMenus(prev => ({ ...prev, [area]: !prev[area] }));

  // Flyout handlers (sidebar colapsado)
  const openFlyout = (area, e) => {
    clearTimeout(flyoutTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyoutCoords({ top: rect.top });
    setFlyoutArea(area);
  };
  const closeFlyout = () => {
    flyoutTimeout.current = setTimeout(() => setFlyoutArea(null), 120);
  };
  const keepFlyout = () => clearTimeout(flyoutTimeout.current);

  const handleLogout = () => { logout(); navigate('/login'); };

  const isCurrentPath = (path) => location.pathname.startsWith(path);

  // Resuelve el label y color del estado actual para mostrarlo en la card
  const statusUI = resolveStatusUI(userStatus, availabilityTypes);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #321c60 0%, #4b2b90 50%, #210056 100%)'
    }}>

      {/* ═══════════════════════════ SIDEBAR ═══════════════════════════ */}
      <aside style={{
        position: 'fixed',
        top: 0, left: 0,
        height: '100%',
        width: sidebarOpen ? '264px' : '72px',
        background: '#160b2e',
        borderRight: '1px solid rgba(134,68,255,0.18)',
        transition: 'width 0.3s ease',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Figtree', 'Inter', sans-serif",
        overflow: 'hidden'
      }}>

        {/* ── Logo + toggle ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarOpen ? 'space-between' : 'center',
          padding: '14px 14px 12px',
          borderBottom: '1px solid rgba(134,68,255,0.15)',
          flexShrink: 0
        }}>
          {sidebarOpen && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img
                src="/assets/images/kio_it.png"
                alt="KIO IT Services"
                style={{ height: '28px', width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
              />
              <img
                src="/assets/images/kloud-pilot.png"
                alt="Kloud Pilot"
                style={{ height: '28px', width: 'auto', objectFit: 'contain' }}
              />
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              width: '28px', height: '28px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {sidebarOpen ? <X size={15} /> : <Menu size={15} />}
          </button>
        </div>

        {/* ── Trigger del dropdown ── */}
        {sidebarOpen ? (
          <button
            ref={userTriggerRef}
            onClick={() => setDropdownOpen(prev => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: '10px',
              padding: '10px 12px',
              background: dropdownOpen ? 'rgba(134,68,255,0.18)' : 'rgba(134,68,255,0.1)',
              border: dropdownOpen ? '1px solid rgba(134,68,255,0.5)' : '1px solid rgba(134,68,255,0.2)',
              borderRadius: '12px',
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'all 0.15s',
              textAlign: 'left',
              width: 'calc(100% - 20px)'
            }}
          >
            {/* Avatar */}
            <div style={{
              width: '36px', height: '36px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8644ff 0%, #4b2b90 100%)',
              border: '2px solid rgba(134,68,255,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '13px', fontWeight: 600, color: '#fff',
              flexShrink: 0, overflow: 'hidden'
            }}>
              {user?.photo
                ? <img src={user.photo} alt={user?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : (getInitials(user?.name) || <User size={15} color="#fff" />)
              }
            </div>

            {/* Info: nombre, email y estado REAL */}
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{
                fontSize: '13px', fontWeight: 600, color: '#fff',
                margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
              }}>
                {user?.name || 'Usuario'}
              </p>
              <p style={{
                fontSize: '11px', color: 'rgba(255,255,255,0.4)',
                margin: '1px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
              }}>
                {user?.email}
              </p>
              {/* Punto + label del estado actual — siempre sincronizado con el backend */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px' }}>
                <div style={{
                  width: '6px', height: '6px',
                  borderRadius: '50%',
                  background: statusUI.color,
                  flexShrink: 0
                }} />
                <span style={{ fontSize: '10px', color: statusUI.color, fontWeight: 500 }}>
                  {statusUI.label}
                </span>
              </div>
            </div>

            <ChevronDown
              size={14}
              color="rgba(255,255,255,0.3)"
              style={{
                transform: dropdownOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
                flexShrink: 0
              }}
            />
          </button>
        ) : (
          // Sidebar colapsado: solo avatar
          <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0', flexShrink: 0, position: 'relative' }}>
            <button
              ref={userTriggerRef}
              onClick={() => setDropdownOpen(prev => !prev)}
              style={{
                width: '40px', height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #8644ff 0%, #4b2b90 100%)',
                border: dropdownOpen ? '2px solid #8644ff' : '2px solid rgba(134,68,255,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: 600, color: '#fff',
                cursor: 'pointer', overflow: 'hidden', padding: 0,
                transition: 'border-color 0.15s',
                position: 'relative'
              }}
            >
              {user?.photo
                ? <img src={user.photo} alt={user?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : (getInitials(user?.name) || <User size={16} color="#fff" />)
              }
              {/* Indicador de estado como punto en la esquina del avatar */}
              <div style={{
                position: 'absolute',
                bottom: '1px', right: '1px',
                width: '9px', height: '9px',
                borderRadius: '50%',
                background: statusUI.color,
                border: '2px solid #160b2e'
              }} />
            </button>
          </div>
        )}

        {/* ── Navegación ── */}
        <nav style={{
          flex: 1,
          padding: '6px 10px',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(134,68,255,0.2) transparent'
        }}>
          {sidebarOpen && (
            <p style={{
              fontSize: '10px', fontWeight: 600,
              color: 'rgba(255,255,255,0.28)',
              letterSpacing: '1px', textTransform: 'uppercase',
              margin: '0 0 8px 4px'
            }}>Áreas</p>
          )}

          {userAreas.filter(area => area !== AREAS.ADMIN).map((area) => {
            const config = AREA_CONFIG[area];
            if (!config) return null;

            const Icon = config.icon;
            const isActive  = isCurrentPath(config.path);
            const isExpanded = expandedMenus[area];
            const accent    = config.activeAccent;

            return (
              <div key={area}>
                <button
                  onClick={() => {
                    if (sidebarOpen) {
                      if (config.hasSubmenu) toggleSubmenu(area);
                      else navigate(config.path);
                    } else {
                      // Colapsado: sin submenú navega directo; con submenú el flyout lo maneja
                      if (!config.hasSubmenu) navigate(config.path);
                    }
                  }}
                  onMouseEnter={e => {
                    if (!isActive) e.currentTarget.style.background = 'rgba(134,68,255,0.07)';
                    if (!sidebarOpen && config.hasSubmenu) openFlyout(area, e);
                  }}
                  onMouseLeave={e => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                    if (!sidebarOpen && config.hasSubmenu) closeFlyout();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: sidebarOpen ? 'flex-start' : 'center',
                    width: '100%',
                    padding: sidebarOpen ? '9px 10px' : '9px 0',
                    borderRadius: '10px',
                    border: isActive ? `1px solid ${accent}44` : '1px solid transparent',
                    background: isActive ? `${accent}18` : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    gap: '10px',
                    marginBottom: '2px',
                    textAlign: 'left'
                  }}
                >
                  <div style={{
                    width: '34px', height: '34px',
                    borderRadius: '9px',
                    background: isActive ? `${accent}28` : config.iconBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'background 0.15s'
                  }}>
                    {config.isImage
                      ? <img
                          src={Icon}
                          alt={config.name}
                          style={{
                            width: '20px', height: '20px',
                            objectFit: 'contain',
                            // Aplica el color del área; más brillante cuando está activo
                            filter: isActive
                              ? config.iconFilter + ' brightness(1.3)'
                              : config.iconFilter
                          }}
                        />
                      : <Icon size={17} color={config.iconColor} />
                    }
                  </div>

                  {sidebarOpen && (
                    <>
                      <span style={{
                        flex: 1,
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? config.activeLabelColor : 'rgba(255,255,255,0.65)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>
                        {config.name}
                      </span>
                      {config.hasSubmenu
                        ? <ChevronDown
                            size={14}
                            color={isActive ? config.activeLabelColor : 'rgba(255,255,255,0.25)'}
                            style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
                          />
                        : isActive && <ChevronRight size={14} color={config.activeLabelColor} style={{ flexShrink: 0 }} />
                      }
                    </>
                  )}
                </button>

                {config.hasSubmenu && isExpanded && sidebarOpen && (
                  <div style={{ paddingLeft: '8px', marginTop: '2px', marginBottom: '4px' }}>
                    {config.submenu
                      .filter(item => (!item.permission || hasPermission(item.permission)) && hasAllowedRole(user, item.roles))
                      .map((item) => {
                        const isSubActive = location.pathname === item.path;
                        return (
                          <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            onMouseEnter={e => {
                              if (!isSubActive) {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                const img = e.currentTarget.querySelector('img');
                                if (img) img.style.filter = 'brightness(0) invert(1) opacity(0.75)';
                                const span = e.currentTarget.querySelector('span');
                                if (span) span.style.color = 'rgba(255,255,255,0.8)';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isSubActive) {
                                e.currentTarget.style.background = 'transparent';
                                const img = e.currentTarget.querySelector('img');
                                if (img) img.style.filter = SUB_FILTER;
                                const span = e.currentTarget.querySelector('span');
                                if (span) span.style.color = 'rgba(255,255,255,0.45)';
                              }
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              width: '100%',
                              padding: '5px 10px 5px 10px',
                              borderRadius: '8px',
                              background: isSubActive ? `${accent}14` : 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              textAlign: 'left'
                            }}
                          >
                            <div style={{
                              width: '26px', height: '26px',
                              borderRadius: '7px',
                              background: isSubActive ? `${accent}20` : 'rgba(255,255,255,0.05)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                              transition: 'background 0.15s'
                            }}>
                              <img
                                src={KIO[item.kioIcon]}
                                alt={item.name}
                                style={{
                                  width: '15px', height: '15px',
                                  objectFit: 'contain',
                                  filter: isSubActive ? SUB_FILTER_ACTIVE : SUB_FILTER
                                }}
                              />
                            </div>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: isSubActive ? 600 : 400,
                              color: isSubActive ? '#fff' : 'rgba(255,255,255,0.45)'
                            }}>{item.name}</span>
                          </button>
                        );
                      })
                    }
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── Cerrar Sesión ── */}
        <div style={{ padding: '10px', borderTop: '1px solid rgba(134,68,255,0.15)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px', flexDirection: sidebarOpen ? 'row' : 'column' }}>
          {hasAllowedRole(user, ['admin']) && (
            <button
              type="button"
              aria-label="Administración"
              title="Administración"
              aria-current={isCurrentPath('/admin') ? 'page' : undefined}
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '40px', height: '40px', flexShrink: 0,
                borderRadius: '8px', border: '1px solid rgba(134,68,255,0.25)',
                background: isCurrentPath('/admin') ? 'rgba(134,68,255,0.18)' : 'transparent',
                color: '#c9a8ff', cursor: 'pointer'
              }}
            >
              <Settings size={18} />
            </button>
          )}
          <button
            title="Cerrar Sesión"
            aria-label="Cerrar Sesión"
            onClick={handleLogout}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,80,80,0.1)'; e.currentTarget.style.color = '#ff5050'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,80,80,0.7)'; }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
              gap: '10px',
              flex: 1,
              minWidth: 0,
              padding: '10px 14px',
              borderRadius: '10px',
              border: 'none',
              background: 'transparent',
              color: 'rgba(255,80,80,0.7)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              transition: 'all 0.15s',
              textAlign: 'left'
            }}
          >
            <LogOut size={16} />
            {sidebarOpen && <span>Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* ── Flyout submenú (sidebar colapsado) via portal ── */}
      {!sidebarOpen && flyoutArea && (() => {
        const cfg = AREA_CONFIG[flyoutArea];
        if (!cfg || !cfg.hasSubmenu) return null;
        const accent = cfg.activeAccent;
        return ReactDOM.createPortal(
          <div
            onMouseEnter={keepFlyout}
            onMouseLeave={closeFlyout}
            style={{
              position: 'fixed',
              top: flyoutCoords.top,
              left: '76px',
              zIndex: 9997,
              background: '#1e1040',
              border: `1px solid ${accent}33`,
              borderRadius: '12px',
              padding: '6px',
              minWidth: '200px',
              boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${accent}22`,
              fontFamily: "'Figtree', 'Inter', sans-serif"
            }}
          >
            {/* Header del flyout: nombre del área */}
            <div style={{
              padding: '6px 10px 8px',
              borderBottom: `1px solid ${accent}22`,
              marginBottom: '4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img
                  src={cfg.icon}
                  alt={cfg.name}
                  style={{ width: '14px', height: '14px', objectFit: 'contain', filter: cfg.iconFilter }}
                />
                <span style={{ fontSize: '11px', fontWeight: 700, color: accent, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  {cfg.name}
                </span>
              </div>
            </div>
            {/* Items del submenú */}
            {cfg.submenu
              .filter(item => (!item.permission || hasPermission(item.permission)) && hasAllowedRole(user, item.roles))
              .map(item => {
                const isSubActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setFlyoutArea(null); }}
                    onMouseEnter={e => {
                      if (!isSubActive) e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                    }}
                    onMouseLeave={e => {
                      if (!isSubActive) e.currentTarget.style.background = 'transparent';
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      width: '100%', padding: '7px 10px',
                      borderRadius: '8px',
                      background: isSubActive ? `${accent}18` : 'transparent',
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                      transition: 'background 0.12s'
                    }}
                  >
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '6px',
                      background: isSubActive ? `${accent}28` : 'rgba(255,255,255,0.05)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <img
                        src={KIO[item.kioIcon]}
                        alt={item.name}
                        style={{
                          width: '13px', height: '13px', objectFit: 'contain',
                          filter: isSubActive ? SUB_FILTER_ACTIVE : SUB_FILTER
                        }}
                      />
                    </div>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: isSubActive ? 600 : 400,
                      color: isSubActive ? '#fff' : 'rgba(255,255,255,0.6)'
                    }}>
                      {item.name}
                    </span>
                  </button>
                );
              })
            }
          </div>,
          document.body
        );
      })()}

      {/* ── Dropdown via portal — nunca recortado por overflow ── */}
      <PortalDropdown
        anchorRef={userTriggerRef}
        open={dropdownOpen}
        onClose={() => setDropdownOpen(false)}
        user={user}
        onStatusChange={(newStatus) => {
          // Actualiza inmediatamente en la card sin esperar al backend
          setUserStatus(newStatus);
        }}
      />

      {/* ═══════════════════════════ MAIN CONTENT ═══════════════════════════ */}
      <main style={{
        marginLeft: sidebarOpen ? '264px' : '72px',
        transition: 'margin-left 0.3s ease',
        minHeight: '100vh'
      }}>
        {children}
      </main>
      <AlertNotifications />
    </div>
  );
}